/**
 * Shared chart shell. The four charts are stacked rows of one panel and all
 * share the x scale, so every rail is a fixed width and the plots line up
 * column-for-column.
 *
 *   left rail   title, (i), per-chart controls
 *   plot        the shared mutagen axis, anchored at 0
 *   right rail  cursor value, or "from ⟶ to" while a selection is active
 *
 * There is no hover tooltip: the right rail already carries every number a
 * tooltip would, and the cursor's own position is tagged on the shared axes.
 *
 * A concrete chart only supplies its lines, its y scale and its formatting.
 */

import { useCallback, useMemo, useRef, type ReactNode } from 'react';

import { AxisBottom, AxisLeft, AxisTop } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleLog } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';

import {
  MUTAGEN_DISPLAY_SCALE,
  formatCompact,
  formatEvo,
  type MilestoneCrossing,
  type RangeSummary,
  type Sample,
} from '../model';
import { Info } from './Info';
import { useLab, type BranchView } from './LabContext';
import { useWheelZoom } from './useWheelZoom';

export interface ChartLine {
  key: string;
  label: string;
  color: string;
  accessor: (sample: Sample) => number;
  dashed?: boolean;
}

export interface ChartFrameProps {
  index: number;
  title: string;
  /** Prose. Lives behind the (i) — never on the surface. */
  info: ReactNode;
  /** A `<PageLink>` to the page that explains this chart at length. */
  infoMore?: ReactNode;
  lines: ChartLine[];
  /** Exact y extent of the visible samples: [min, max], no padding. */
  yDomain: (samples: Sample[]) => [number, number];
  yScaleType?: 'linear' | 'log';
  /**
   * Tick count hint for the y axis. Worth overriding on a log axis: d3 hands
   * back whole decades only when the domain holds at least this many of them,
   * and subdivides into 2·, 3·, … 9· otherwise — which at four is a wall of
   * overlapping labels on any range narrower than four decades.
   */
  yNumTicks?: number;
  formatY: (value: number) => string;
  yLabel: string;
  height?: number;
  controls?: ReactNode;
  /**
   * The scenario constants this row's curve is drawn from — shown at the top of
   * the right rail while tuning, above the values they produce. A knob and the
   * line it bends are then in the same glance, which is the whole reason they
   * are not in one panel above all four rows.
   */
  params?: ReactNode;
  /**
   * Pinned to the *bottom* of the right rail, under everything else. Used by
   * the last row while tuning, which puts it at the bottom right of the whole
   * chart panel — the far corner from the zoom overlay, and the one spot that
   * belongs to all four rails rather than to any one of them.
   */
  railFoot?: ReactNode;
  /**
   * Extra right-rail content, shown only while a selection is active. Called
   * once per selected branch — an untagged figure sitting under two tagged
   * ones would read as a total rather than as one branch's.
   */
  railExtra?: (summary: RangeSummary, view: BranchView) => ReactNode;
  /** Fill the area under the first line inside the selection. */
  shadeSelection?: boolean;
  showTopAxis?: boolean;
  showBottomAxis?: boolean;
}

/**
 * A chart's own dashed line — attack speed, on the strength chart. Shared with
 * the legend chip, so the two cannot drift apart. Only in force when a single
 * branch is selected: while comparing, the branch owns the dash channel.
 */
const LINE_DASH = '5 4';

const MARGIN = { top: 8, right: 16, bottom: 8, left: 64 };
const TOP_AXIS_HEIGHT = 22;
const BOTTOM_AXIS_HEIGHT = 26;
const PARENT_STYLES = { position: 'relative' as const, width: '100%', height: '100%' };

/** Milestones vanish once the whole set is squeezed into this share of the plot. */
const MILESTONE_MIN_SPAN_FRACTION = 0.12;
/** Two guide lines closer than this are pure clutter; keep only the first. */
const MILESTONE_MIN_LINE_GAP = 3;

export function ChartFrame(props: ChartFrameProps) {
  const height =
    (props.height ?? 190) +
    (props.showTopAxis ? TOP_AXIS_HEIGHT : 0) +
    (props.showBottomAxis ? BOTTOM_AXIS_HEIGHT : 0);

  return (
    <section className="chart-row" aria-label={props.title}>
      <div className="rail rail-left">
        <div className="rail-title">
          <span className="chart-index">{props.index}</span>
          <h2>{props.title}</h2>
          <Info title={props.title} more={props.infoMore}>
            {props.info}
          </Info>
        </div>
        {props.controls ? <div className="rail-controls">{props.controls}</div> : null}
      </div>

      <div className="chart-body" style={{ height }}>
        <ParentSize debounceTime={0} parentSizeStyles={PARENT_STYLES}>
          {({ width }) =>
            width > 0 ? <ChartCanvas {...props} width={width} height={height} /> : null
          }
        </ParentSize>
      </div>

      <ChartRailRight {...props} />
    </section>
  );
}

/**
 * A chart row for a quantity the current branch does not have.
 *
 * Drawn instead of dropping the row, so the four rows stay in the same places
 * when you switch branches — a chart that vanishes reads as a layout glitch,
 * whereas one that says what happened to it is the point of the comparison.
 */
export function ChartNoticeRow({
  index,
  title,
  info,
  infoMore,
  params,
  children,
}: {
  index: number;
  title: string;
  info: ReactNode;
  infoMore?: ReactNode;
  /**
   * The row's constants, same as on a drawn chart. A scenario tuned to zero
   * lands here, and dropping its control with the plot would leave nothing on
   * the page able to bring the curve back.
   */
  params?: ReactNode;
  /** The notice itself: what was removed, and what replaced it. */
  children: ReactNode;
}) {
  return (
    <section className="chart-row chart-row-notice" aria-label={title}>
      <div className="rail rail-left">
        <div className="rail-title">
          <span className="chart-index">{index}</span>
          <h2>{title}</h2>
          <Info title={title} more={infoMore}>
            {info}
          </Info>
        </div>
      </div>
      <div className="chart-body chart-notice">{children}</div>
      <div className="rail rail-right">
        {params ? (
          <div className="rail-params" role="group" aria-label={`${title} parameters`}>
            {params}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Long enough for `6 4` to show a gap and `2 3` to show several. */
const MARK_WIDTH = 22;
const MARK_STROKE = 2;

/**
 * A stroke, as a legend chip: the colour and the dash pattern the line is
 * actually drawn with.
 *
 * An SVG line rather than the CSS dashed border this used to be. A border has
 * only `solid` and `dashed`, so the two dashed branches — `6 4` and `2 3` — came
 * out identical, which is the whole job of the chip; and over 13px a 2px dashed
 * border barely reads as dashed at all. Here the `strokeDasharray` is the same
 * string the plot is given, so the legend cannot disagree with the lines.
 */
export function BranchMark({ dash, color }: { dash: string | undefined; color?: string }) {
  return (
    <svg
      className="branch-mark"
      width={MARK_WIDTH}
      height={MARK_STROKE}
      viewBox={`0 0 ${MARK_WIDTH} ${MARK_STROKE}`}
      aria-hidden="true"
    >
      <line
        x1={0}
        y1={MARK_STROKE / 2}
        x2={MARK_WIDTH}
        y2={MARK_STROKE / 2}
        stroke={color ?? 'currentColor'}
        strokeWidth={MARK_STROKE}
        strokeDasharray={dash}
      />
    </svg>
  );
}

/** One value, or a `from ⟶ to` span when a range is selected. */
function RailValue({
  line,
  summary,
  cursorSample,
  formatY,
}: {
  line: ChartLine;
  summary: RangeSummary | null;
  cursorSample: Sample | null;
  formatY: (value: number) => string;
}) {
  // A point selection has one value to report, not a span.
  if (summary && summary.mutagen > 0) {
    return (
      <span className="readout-range">
        <b>{formatY(line.accessor(summary.from))}</b>
        <span className="arrow">⟶</span>
        <b>{formatY(line.accessor(summary.to))}</b>
      </span>
    );
  }
  return (
    <b className="readout-value">
      {summary
        ? formatY(line.accessor(summary.to))
        : cursorSample
          ? formatY(line.accessor(cursorSample))
          : '–'}
    </b>
  );
}

function ChartRailRight({ lines, formatY, railExtra, params, railFoot, title }: ChartFrameProps) {
  const { views, comparing } = useLab();

  return (
    <div className="rail rail-right">
      {params ? (
        <div className="rail-params" role="group" aria-label={`${title} parameters`}>
          {params}
        </div>
      ) : null}
      {lines.map((line) => (
        <div className="readout" key={line.key}>
          <span className="readout-label">
            {/* The stroke, not a colour block: attack speed is drawn dashed and
                a square said nothing about that. Mirrors the plot exactly —
                while comparing, dash means branch, so the quantity goes solid
                here for the same reason it does there. */}
            <BranchMark
              dash={!comparing && line.dashed ? LINE_DASH : undefined}
              color={line.color}
            />
            {line.label}
          </span>
          {/* Comparing gives every branch its own value under the one label,
              tagged with the stroke it is drawn with. */}
          {comparing ? (
            views.map((view) => (
              <span className="readout-branch" key={view.ruleset.id}>
                <span className="readout-branch-name" style={{ color: line.color }}>
                  <BranchMark dash={view.dash} />
                  {view.ruleset.label}
                </span>
                <RailValue
                  line={line}
                  summary={view.selectionSummary}
                  cursorSample={view.cursorSample}
                  formatY={formatY}
                />
              </span>
            ))
          ) : (
            <RailValue
              line={line}
              summary={views[0].selectionSummary}
              cursorSample={views[0].cursorSample}
              formatY={formatY}
            />
          )}
        </div>
      ))}
      {railExtra
        ? views.map((view) =>
            view.selectionSummary ? (
              <div className="readout-extra" key={view.ruleset.id}>
                {comparing ? (
                  <span className="readout-branch-name">
                    <BranchMark dash={view.dash} />
                    {view.ruleset.label}
                  </span>
                ) : null}
                {railExtra(view.selectionSummary, view)}
              </div>
            ) : null,
          )
        : null}
      {railFoot ? <div className="rail-foot">{railFoot}</div> : null}
    </div>
  );
}

/** Estimated rendered width of a 9.5px milestone label. */
const labelWidth = (text: string) => text.length * 5.3 + 10;

/**
 * Decide which milestone guides survive at the current zoom. Lines are cheap so
 * nearly all of them are drawn, but labels are placed most-important-first and
 * only where they do not collide — and the whole layer disappears once the set
 * is crushed into the left edge.
 */
function planMilestones(
  crossings: MilestoneCrossing[],
  xScale: (mutagen: number) => number,
  innerWidth: number,
): { crossing: MilestoneCrossing; x: number; showLabel: boolean }[] {
  const inRange = crossings.filter((c) => c.primary && xScale(c.mutagen) <= innerWidth);
  if (inRange.length === 0) return [];

  if (inRange.length > 1) {
    const xs = inRange.map((c) => xScale(c.mutagen));
    const span = Math.max(...xs) - Math.min(...xs);
    if (span < innerWidth * MILESTONE_MIN_SPAN_FRACTION) return [];
  }

  const byPosition = [...inRange].sort((a, b) => a.mutagen - b.mutagen);
  const lines: { crossing: MilestoneCrossing; x: number; showLabel: boolean }[] = [];
  for (const crossing of byPosition) {
    const x = xScale(crossing.mutagen);
    const previous = lines[lines.length - 1];
    if (previous && x - previous.x < MILESTONE_MIN_LINE_GAP) continue;
    lines.push({ crossing, x, showLabel: false });
  }

  // Later milestones matter more, so hand out label slots from the right.
  const placed: [number, number][] = [];
  for (const entry of [...lines].sort((a, b) => b.crossing.evo - a.crossing.evo)) {
    const left = entry.x + 4;
    const right = left + labelWidth(entry.crossing.label);
    if (right > innerWidth) continue;
    if (placed.some(([l, r]) => left < r && right > l)) continue;
    placed.push([left, right]);
    entry.showLabel = true;
  }

  return lines;
}

function ChartCanvas({
  lines,
  yDomain,
  yScaleType = 'linear',
  yNumTicks = 4,
  formatY,
  yLabel,
  width,
  height,
  shadeSelection,
  showTopAxis,
  showBottomAxis,
}: ChartFrameProps & { width: number; height: number }) {
  const {
    domain,
    series,
    views,
    comparing,
    crossings,
    evoTicks,
    cursor,
    cursorSample,
    setCursor,
    selection,
    dragging,
    beginDrag,
    updateDrag,
    endDrag,
    zoomBy,
    resetZoom,
  } = useLab();

  const svgRef = useRef<SVGSVGElement | null>(null);
  const top = MARGIN.top + (showTopAxis ? TOP_AXIS_HEIGHT : 0);
  const innerWidth = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(
    10,
    height - top - MARGIN.bottom - (showBottomAxis ? BOTTOM_AXIS_HEIGHT : 0),
  );

  const xScale = useMemo(
    () => scaleLinear<number>({ domain: [domain[0], domain[1]], range: [0, innerWidth] }),
    [domain, innerWidth],
  );

  // The y extent has to hold every branch on screen, or the one plotted second
  // would be silently clipped rather than compared.
  const [yLo, yHi] = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const view of views) {
      const [a, b] = yDomain(view.series);
      if (Number.isFinite(a)) lo = Math.min(lo, a);
      if (Number.isFinite(b)) hi = Math.max(hi, b);
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1] as [number, number];
    // Degenerate extent (a flat line) still needs a drawable band.
    if (!(hi > lo)) {
      const pad = Math.max(Math.abs(hi) * 0.1, 0.1);
      return [lo - pad, hi + pad] as [number, number];
    }
    return [lo, hi] as [number, number];
  }, [yDomain, views]);

  // The y domain is the exact extent of the visible data — no padding, no
  // nicing. Breathing room comes out of the *range* instead, purely so a 2px
  // stroke sitting on the extreme value is not sliced in half by the edge.
  const yRange = useMemo<[number, number]>(() => [innerHeight - 1, 2], [innerHeight]);

  const yScale = useMemo(() => {
    if (yScaleType === 'log') {
      return scaleLog<number>({
        domain: [Math.max(yLo, 1e-6), Math.max(yHi, Math.max(yLo, 1e-6) * 1.1)],
        range: yRange,
        clamp: true,
      });
    }
    return scaleLinear<number>({ domain: [yLo, yHi], range: yRange, clamp: true });
  }, [yScaleType, yLo, yHi, yRange]);

  /** Screen x (client coords) -> raw mutagen, clamped to the plot area. */
  const toMutagen = useCallback(
    (clientX: number) => {
      const node = svgRef.current;
      if (!node) return null;
      const rect = node.getBoundingClientRect();
      const x = clientX - rect.left - MARGIN.left;
      return xScale.invert(Math.min(Math.max(x, 0), innerWidth));
    },
    [innerWidth, xScale],
  );

  // The wheel wants the node the moment it exists; `toMutagen` wants it later,
  // on every pointer event. One callback ref feeds both.
  const setWheelNode = useWheelZoom(zoomBy);
  const attachSvg = useCallback(
    (node: SVGSVGElement | null) => {
      svgRef.current = node;
      setWheelNode(node);
    },
    [setWheelNode],
  );

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const mutagen = toMutagen(event.clientX);
    if (mutagen === null) return;
    // Pointer capture is unavailable on SVG in some engines; the drag still
    // works without it, so never let a missing API break the interaction.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    beginDrag(mutagen);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const mutagen = toMutagen(event.clientX);
    if (mutagen === null) return;
    setCursor(mutagen);
    if (dragging) updateDrag(mutagen);
  };

  const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    endDrag();
  };

  const cursorX = cursor === null ? null : xScale(cursor);
  const cursorInView = cursor !== null && cursor >= domain[0] && cursor <= domain[1];
  const showCursor = cursorInView && cursorX !== null && cursorSample !== null;

  const selectionPixels = useMemo(() => {
    if (!selection) return null;
    const lo = Math.min(selection[0], selection[1]);
    const hi = Math.max(selection[0], selection[1]);
    if (hi < domain[0] || lo > domain[1]) return null;
    const x0 = Math.min(Math.max(xScale(lo), 0), innerWidth);
    const x1 = Math.min(Math.max(xScale(hi), 0), innerWidth);
    return { x0, x1, lo, hi };
  }, [selection, xScale, innerWidth, domain]);

  const selectionSamples = useMemo(() => {
    if (!selectionPixels || !shadeSelection) return [];
    return series.filter((s) => s.mutagen >= selectionPixels.lo && s.mutagen <= selectionPixels.hi);
  }, [series, selectionPixels, shadeSelection]);

  const milestones = useMemo(
    () => planMilestones(crossings, xScale, innerWidth),
    [crossings, xScale, innerWidth],
  );

  return (
    <svg
      ref={attachSvg}
      width={width}
      height={height}
      className="chart-svg"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => {
        setCursor(null);
        if (dragging) endDrag();
      }}
      onDoubleClick={resetZoom}
    >
      <Group left={MARGIN.left} top={top}>
        <GridRows
          scale={yScale}
          width={innerWidth}
          height={innerHeight}
          stroke="#222834"
          numTicks={4}
        />

        {/* Vertical guides at the evolution ticks of the top axis. */}
        {evoTicks.map((tick) => (
          <line
            key={`evo-${tick.evo}`}
            className="evo-guide"
            x1={xScale(tick.mutagen)}
            x2={xScale(tick.mutagen)}
            y1={0}
            y2={innerHeight}
          />
        ))}

        {/* A range gets a band; a point selection gets a pinned marker line. */}
        {selectionPixels ? (
          selectionPixels.x1 > selectionPixels.x0 ? (
            <rect
              x={selectionPixels.x0}
              y={0}
              width={selectionPixels.x1 - selectionPixels.x0}
              height={innerHeight}
              className="selection-band"
            />
          ) : (
            <line
              className="selection-point"
              x1={selectionPixels.x0}
              x2={selectionPixels.x0}
              y1={0}
              y2={innerHeight}
            />
          )
        ) : null}

        {milestones.map(({ crossing, x, showLabel }) => (
          <g key={crossing.label} className="milestone">
            <line x1={x} x2={x} y1={0} y2={innerHeight} />
            {showLabel ? (
              <text x={x + 4} y={10} className="milestone-label">
                {crossing.label}
              </text>
            ) : null}
          </g>
        ))}

        {shadeSelection && selectionSamples.length > 1 ? (
          <AreaClosed
            data={selectionSamples}
            x={(d) => xScale(d.mutagen)}
            y={(d) => yScale(lines[0].accessor(d))}
            yScale={yScale}
            curve={curveMonotoneX}
            fill={lines[0].color}
            fillOpacity={0.24}
            stroke="none"
          />
        ) : null}

        {/* Colour is the quantity, dash is the branch. While comparing, the
            branch wins the dash channel — a chart's own dashed line (attack
            speed) goes solid so that "dashed" never means two things at once. */}
        {views.map((view) =>
          lines.map((line) => (
            <LinePath
              key={`${view.ruleset.id}-${line.key}`}
              data={view.series}
              x={(d) => xScale(d.mutagen)}
              y={(d) => yScale(line.accessor(d))}
              stroke={line.color}
              strokeWidth={2}
              strokeOpacity={comparing && !view.primary ? 0.85 : 1}
              strokeDasharray={comparing ? view.dash : line.dashed ? LINE_DASH : undefined}
              curve={curveMonotoneX}
              shapeRendering="geometricPrecision"
            />
          )),
        )}

        {showCursor ? (
          <g className="cursor">
            <line x1={cursorX} x2={cursorX} y1={0} y2={innerHeight} />
            {views.map((view) =>
              view.cursorSample === null
                ? null
                : lines.map((line) => (
                    <circle
                      key={`${view.ruleset.id}-${line.key}`}
                      cx={cursorX}
                      cy={yScale(line.accessor(view.cursorSample as Sample))}
                      r={3.5}
                      fill={view.primary ? line.color : '#0d1016'}
                      stroke={view.primary ? '#0d1016' : line.color}
                      strokeWidth={1.5}
                    />
                  )),
            )}
          </g>
        ) : null}

        {showTopAxis ? (
          <>
            <AxisTop
              top={0}
              scale={xScale}
              tickValues={evoTicks.map((tick) => tick.mutagen)}
              tickFormat={(_value, index) => formatEvo(evoTicks[index]?.evo ?? 0, 0)}
              stroke="#39424f"
              tickStroke="#39424f"
              tickLabelProps={() => ({
                fill: '#7f8ea8',
                fontSize: 10,
                textAnchor: 'middle',
                dy: '-0.2em',
              })}
            />
            {showCursor ? (
              <CursorTag
                x={cursorX}
                y={-16}
                width={innerWidth}
                text={formatEvo(cursorSample.evo)}
              />
            ) : null}
          </>
        ) : null}

        {showBottomAxis ? (
          <>
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              numTicks={6}
              tickFormat={(value) => formatCompact(Number(value) * MUTAGEN_DISPLAY_SCALE, 1)}
              stroke="#39424f"
              tickStroke="#39424f"
              tickLabelProps={() => ({ fill: '#8b97ad', fontSize: 10, textAnchor: 'middle' })}
            />
            {showCursor ? (
              <CursorTag
                x={cursorX}
                y={innerHeight + 5}
                width={innerWidth}
                text={formatCompact(cursorSample.displayMutagen, 2)}
              />
            ) : null}
          </>
        ) : null}

        <AxisLeft
          scale={yScale}
          numTicks={yNumTicks}
          tickFormat={(value) => formatY(Number(value))}
          stroke="#39424f"
          tickStroke="#39424f"
          tickLabelProps={() => ({
            fill: '#8b97ad',
            fontSize: 10,
            textAnchor: 'end',
            dx: '-0.25em',
            dy: '0.25em',
          })}
        />
      </Group>

      <text x={4} y={top - 6} className="axis-title">
        {yLabel}
      </text>
    </svg>
  );
}

/**
 * The cursor's own position, tagged onto the shared axes. This is what a
 * tooltip used to carry that the right rail cannot: where on x you are.
 */
function CursorTag({
  x,
  y,
  width,
  text,
}: {
  x: number;
  y: number;
  width: number;
  text: string;
}) {
  const half = Math.max(text.length * 3.4 + 7, 17);
  const cx = Math.min(Math.max(x, half), Math.max(width - half, half));
  return (
    <g className="cursor-tag" transform={`translate(${cx}, ${y})`}>
      <rect x={-half} y={0} width={half * 2} height={14} rx={3} />
      <text x={0} y={10.5} textAnchor="middle">
        {text}
      </text>
    </g>
  );
}
